//#region importsSection
import React, { useEffect, useState } from "react";
import {
  Button,
  Spinner,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Card,
  CardBody,
} from "reactstrap";
import { GetLanguageString } from "../helper/Components";
import swal from "sweetalert";
import Draggable from "react-draggable";
import { useTranslation } from "react-i18next";
import { LoadingSpinner } from "../../constants/const";
import { ApiFund } from "../funds/ApiFund";
import { ApiDocuments } from "../documents/ApiDocuments";
//#endregion

//#region styles (Pattern matched to reference)
const labelStyle = {
  fontSize: "12px",
  color: "#6B7280",
  marginBottom: "2px",
  lineHeight: "1.15",
};

const valueStyle = {
  fontSize: "14px",
  fontWeight: 500,
  color: "#1F2937",
  lineHeight: "1.25",
};

const cardBase = {
  borderRadius: "12px",
  border: "1px solid #E5E7EB",
};

const sectionHeader = (color) => ({
  fontSize: "12px",
  fontWeight: 600,
  letterSpacing: "0.04em",
  color,
  textTransform: "uppercase",
  marginBottom: "10px",
});

const animatedBody = {
  animation: "fadeSlideIn 0.25s ease-out",
};

const amountBadge = (bgColor = "#D1FAE5", textColor = "#059669") => ({
  display: "inline-block",
  padding: "2px 8px",
  borderRadius: "12px",
  backgroundColor: bgColor,
  color: textColor,
  fontWeight: 600,
  fontSize: "13px",
});
//#endregion

const FundsInfaqAmanatViewForm = ({
  setViewInfaqAmanatForm,
  notificationId,
}) => {
  const { t } = useTranslation();
  const [notificationresponse, setNotificationresponse] = useState({});
  const [loading, setLoading] = useState(false);
  const [viewButtonDisable, setViewButtonDisable] = useState(false);
  const [buttonDisable, setButtonDisable] = useState(false);
  const toggle = () => setViewInfaqAmanatForm(false);

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const handleDrag = (e, ui) =>
    setPosition((p) => ({ x: p.x + ui.deltaX, y: p.y + ui.deltaY }));

  useEffect(() => {
    if (!notificationId) return;
    setLoading(true);
    ApiFund()
      .getInfaqRequestDetail(notificationId)
      .then((res) => {
        if (res.data) setNotificationresponse(res.data || {});
      })
      .finally(() => setLoading(false));
  }, [notificationId]);

  const openFile = (fileUrl) => {
    setViewButtonDisable(true);
    try {
      window.open(fileUrl, "_blank");
    } catch (err) {
      swal({ title: "Error opening file", icon: "error", buttons: "OK" });
    } finally {
      setViewButtonDisable(false);
    }
  };

  const downloadFile = async (fileId) => {
    setButtonDisable(true);
    try {
      const response = await ApiDocuments().downloadDocument(fileId);
      if (response.status === 200) {
        window.location.href = `${response.data.downloadLink}&confirm=t`;
      } else {
        swal({ title: t(response.data), icon: "error", buttons: "OK" });
      }
    } catch (error) {
      swal({ title: error.message, icon: "error", buttons: "OK" });
    } finally {
      setButtonDisable(false);
    }
  };

  return (
    <>
      <style>
        {`
          @keyframes fadeSlideIn {
            from { opacity: 0; transform: translateY(8px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>

      <Draggable position={position} onDrag={handleDrag} cancel=".no-drag">
        <Modal size="xl" isOpen className="inmodal">
          <ModalHeader className="modal-title">
            <GetLanguageString props="common_changes" />
          </ModalHeader>

          {loading ? (
            <ModalBody className="text-center py-4">
              <LoadingSpinner />
            </ModalBody>
          ) : (
            <ModalBody style={animatedBody}>
              {/* MEMBER & ACCOUNT INFO SECTION */}
              <Card
                className="mb-3"
                style={{
                  ...cardBase,
                  background: "#EFF6FF",
                  borderLeft: "5px solid #3B82F6",
                }}
              >
                <CardBody>
                  <div style={sectionHeader("#2563EB")}>
                    Request Information
                  </div>
                  <Row className="gy-3">
                    <Col md="4">
                      <div style={labelStyle}>Member Name</div>
                      <div className="ur" style={valueStyle}>
                        {notificationresponse.memberName}
                      </div>
                    </Col>
                    <Col md="4">
                      <div style={labelStyle}>To Bank Account</div>
                      <div style={valueStyle}>
                        {notificationresponse.toBankAccount}
                      </div>
                    </Col>
                    <Col md="4">
                      <div style={labelStyle}>Account Head</div>
                      <div style={valueStyle}>
                        {notificationresponse.accountHead}
                      </div>
                    </Col>
                    <Col md="4">
                      <div style={labelStyle}>Reference</div>
                      <div style={valueStyle}>
                        {notificationresponse.reference}
                      </div>
                    </Col>
                    <Col md="4">
                      <div style={labelStyle}>Date</div>
                      <div style={valueStyle}>{notificationresponse.date}</div>
                    </Col>
                    <Col md="4">
                      <div style={labelStyle}>Total Amount</div>
                      <div style={amountBadge("#DBEAFE", "#2563EB")}>
                        {new Intl.NumberFormat().format(
                          notificationresponse.amount || 0
                        )}
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              {/* ATTACHMENT SECTION */}
              {notificationresponse.approvalFile && (
                <Card
                  className="mb-3"
                  style={{
                    ...cardBase,
                    background: "#F0F9FF",
                    borderLeft: "5px solid #0EA5E9",
                  }}
                >
                  <CardBody className="d-flex justify-content-between align-items-center">
                    <div>
                      <div style={sectionHeader("#0284C7")}>
                        <GetLanguageString props="attachment" />
                      </div>
                      <div style={valueStyle}>Approval Document</div>
                    </div>

                    <div className="no-drag">
                      <Button
                        color="white"
                        disabled={viewButtonDisable}
                        size="sm"
                        className="me-2 shadow-sm border"
                        onClick={() =>
                          openFile(notificationresponse.approvalFile)
                        }
                      >
                        {viewButtonDisable ? (
                          <Spinner size="sm" color="dark" />
                        ) : (
                          <>
                            <i className="fa fa-eye me-1 text-primary"></i>
                            <GetLanguageString props="member_add_rafiq_request_view_document" />
                          </>
                        )}
                      </Button>
                      <Button
                        color="white"
                        size="sm"
                        className="shadow-sm border"
                        disabled={buttonDisable}
                        onClick={() =>
                          downloadFile(notificationresponse.approvalFileDriveId)
                        }
                      >
                        {buttonDisable ? (
                          <Spinner size="sm" color="dark" />
                        ) : (
                          <>
                            <i className="fa fa-download me-1 text-success"></i>
                            <GetLanguageString props="member_add_rafiq_request_download_document" />
                          </>
                        )}
                      </Button>
                    </div>
                  </CardBody>
                </Card>
              )}
            </ModalBody>
          )}

          <ModalFooter>
            <Button color="primary" onClick={toggle}>
              <GetLanguageString props="users_role_ok_btn" />
            </Button>
          </ModalFooter>
        </Modal>
      </Draggable>
    </>
  );
};

export default FundsInfaqAmanatViewForm;
